import Table from "common/Table/Table";
import { PrecisionConfig } from "types";
import React, { useContext } from "react";
import { ColumnsType } from "antd/es/table";
import { ExchangesIconsGroup } from "features/portfolio/Assets/ExchangesIconsGroup";
import { RootState } from "app/store";
import Link from "common/Typography/Link";
import SellCoinAction from "features/portfolio/Assets/SellCoinAction";
import { HelperTooltip } from "common/Tooltips/HelperTooltip";
import {
  sortByNumberProperty,
  sortByStringProperty,
} from "@shared/utils/general";
import TextWithPrecisionAndIcon from "common/Text/TextWithPrecisionAndIcon";
import { ICON_TYPES } from "@shared/common/constants";
import {
  ConsolidatedCoinData,
  LanguageNameSpaces,
  PrecisionType,
} from "@shared/types";
import { TableLoadingEmpty } from "common/styles";
import { DrawerContext } from "common/Drawers/Drawer.context.ts";
import TextWithIcon from "common/Text/TextWithIcon";
import { useTheme } from "styled-components";
import useAssetItem from "features/portfolio/common/useAssetItem.tsx";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { useIsSubUser, useSelectUser } from "@shared/utils/hooks/misc.ts";
import {
  FROM_LAPTOP_COLUMN,
  FROM_TABLET_COLUMN,
} from "common/NewLook/responsiveColumns.ts";

interface CoinsTableProps {
  data: ConsolidatedCoinData[];
  loading: boolean;
  clientToExchangeMapping?: Record<string, string>;
  precisionSelector: (state: RootState) => PrecisionConfig | undefined;
  emptyState?: React.JSX.Element;
}

const AssetsTable = ({
  data,
  loading,
  clientToExchangeMapping,
  precisionSelector,
  emptyState,
}: CoinsTableProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.PORTFOLIO);
  const { isDrawer } = useContext(DrawerContext) || {};
  const {
    totalHeader,
    availableHeader,
    getQuantity,
    getPrice,
    getPriceChange,
    getNewLookPriceTrend,
    getTotalAmount,
    openCoinExtraDetails,
  } = useAssetItem({ precisionSelector });
  const isSubUser = useIsSubUser();
  const user = useSelectUser();
  const { isNewLook, typography } = useTheme();
  const newLookSymbolWeight = typography.fontWeights.SEMI_BOLD;
  const numberAlign = isNewLook ? "right" : undefined;

  const columns: ColumnsType<ConsolidatedCoinData> = [
    {
      title: t("assets.coin"),
      dataIndex: "symbol",
      key: "symbol",
      showSorterTooltip: false,
      sorter: (a, b) => sortByStringProperty(a, b, "symbol"),
      render: (symbol) =>
        isNewLook ? (
          <TextWithIcon
            text={symbol}
            iconType={ICON_TYPES.COIN}
            coinOrExchangeName={symbol}
            pxIconSize={24}
            textStyle={{ fontWeight: newLookSymbolWeight }}
          />
        ) : (
          <TextWithIcon
            text={symbol}
            iconType={ICON_TYPES.COIN}
            coinOrExchangeName={symbol}
            iconSize="xl"
            textStyle={{ top: 1, position: "relative" }}
          />
        ),
    },
    {
      title: t("assets.total"),
      dataIndex: totalHeader,
      align: numberAlign,
      showSorterTooltip: false,
      defaultSortOrder: "descend",
      render: (_, coinData) => getTotalAmount(coinData),
      sorter: (a, b) => sortByNumberProperty(a, b, "total_in_usd"),
    },
    {
      title: (
        <div
          style={{
            display: "flex",
            justifyContent: isNewLook ? "flex-end" : undefined,
          }}
        >
          {t("assets.available")}
          <HelperTooltip
            analyticsPageName="Coins"
            analyticsFieldName={t("assets.available")}
            description={t("assets.available_tooltip")}
          />
        </div>
      ),
      dataIndex: availableHeader,
      align: numberAlign,
      showSorterTooltip: false,
      render: (available) => (
        <TextWithPrecisionAndIcon
          precisionSelector={precisionSelector}
          precisionType={PrecisionType.AMOUNT}
          text={available}
          round={true}
        />
      ),
      sorter: (a, b) => sortByNumberProperty(a, b, availableHeader),
    },
    {
      title: t("assets.quantity"),
      key: "quantity",
      dataIndex: "quantity",
      align: numberAlign,
      ...(isNewLook && FROM_LAPTOP_COLUMN),
      showSorterTooltip: false,
      render: (quantity, { symbol }) => getQuantity(quantity, symbol),
      sorter: (a, b) => sortByNumberProperty(a, b, "quantity"),
    },
    {
      title: t("assets.price"),
      key: "price",
      align: numberAlign,
      showSorterTooltip: false,
      render: (_, coinData) => getPrice(coinData),
      sorter: (a, b) => sortByNumberProperty(a, b, "btc_price"),
    },
    {
      title: t("assets.price_24h"),
      dataIndex: "graph_data",
      align: numberAlign,
      ...(isNewLook && FROM_LAPTOP_COLUMN),
      showSorterTooltip: false,
      render: (_, coinData) =>
        isNewLook ? getNewLookPriceTrend(coinData) : getPriceChange(coinData),
      sorter: (a, b) => sortByNumberProperty(a, b, "graph_data.change[0]"),
      width: isNewLook ? 168 : 150,
    },
  ];

  if (!isDrawer) {
    columns.splice(1, 0, {
      title: t("assets.accounts"),
      dataIndex: "clients",
      key: "clients",
      showSorterTooltip: false,
      ...(isNewLook && FROM_TABLET_COLUMN),
      render: (clients, _) => (
        <ExchangesIconsGroup
          clients={clients}
          clientToExchangeMapping={clientToExchangeMapping}
        />
      ),
    });
    if (!isSubUser && !user?.isWhopExperience()) {
      columns.push({
        title: "",
        dataIndex: "actions",
        key: "action",
        width: 40,
        render: (_, coinData) => {
          return (
            <SellCoinAction
              coinData={coinData}
              isPosition={false}
              trigger={<Link>{t("assets.sell")}</Link>}
            />
          );
        },
      });
    }
  }

  const isRowClickable = !isDrawer;

  return (
    <Table<ConsolidatedCoinData>
      columns={columns}
      dataSource={data}
      loading={loading}
      pagination={{ pageSizeOptions: ["10", "25"] }}
      rowClassName={`table-half-padding${
        isRowClickable ? " u-cursorPointer" : ""
      }`}
      onRow={(coinData) => {
        return {
          onClick: () => openCoinExtraDetails?.(coinData),
        };
      }}
      locale={{ emptyText: emptyState || <TableLoadingEmpty /> }}
    />
  );
};

export default AssetsTable;
